import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

// Zod schemas for input validation
const QuestionnaireResponseSchema = z.object({
  key: z.string(),
  answer: z.string().optional(),
});

export const listConsultationTypes = createServerFn({ method: "GET" })
  .handler(async () => {
    const { data, error } = await supabase
      .from("consultation_types")
      .select("*")
      .eq("is_active", true)
      .order("price_paise", { ascending: true });

    if (error) throw error;
    return data;
  });

export const listConsultants = createServerFn({ method: "GET" })
  .handler(async () => {
    const { data, error } = await supabase
      .from("consultants")
      .select("*")
      .eq("is_active", true);

    if (error) throw error;
    return data;
  });

export const getConsultantAvailability = createServerFn({ method: "GET" })
  .inputValidator((data) => z.object({ consultantId: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => {
    const { data: availability, error: availError } = await supabase
      .from("consultant_availability")
      .select("*")
      .eq("consultant_id", data.consultantId)
      .eq("is_active", true);

    if (availError) throw availError;

    const { data: blocked, error: blockedError } = await supabase
      .from("consultant_blocked_dates")
      .select("*")
      .eq("consultant_id", data.consultantId)
      .gte("end_time", new Date().toISOString());

    if (blockedError) throw blockedError;

    const { data: booked, error: bookedError } = await supabase
      .from("appointments")
      .select("start_time, end_time")
      .eq("consultant_id", data.consultantId)
      .in("status", ["pending", "confirmed", "completed", "rescheduled"])
      .gte("start_time", new Date().toISOString());

    if (bookedError) throw bookedError;

    return { availability, blocked, booked };
  });

export const bookAppointment = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({
    customerId: z.string().uuid(),
    consultantId: z.string().uuid(),
    consultationTypeId: z.string().uuid(),
    startTime: z.string(), // ISO string (UTC)
    mealPlanId: z.string().uuid().optional(),
    customerNotes: z.string().optional(),
    responses: z.array(QuestionnaireResponseSchema).optional(),
  }).parse(data))
  .handler(async ({ data }) => {
    const { data: appointment, error } = await (supabase as any).rpc("book_appointment", {
      p_customer_id: data.customerId,
      p_consultant_id: data.consultantId,
      p_consultation_type_id: data.consultationTypeId,
      p_start_time: data.startTime,
      p_meal_plan_id: data.mealPlanId || null,
      p_customer_notes: data.customerNotes || null,
      p_responses: data.responses || []
    });

    if (error) {
      if (error.message.includes("consultant_not_available")) {
        throw new Error("This appointment slot is no longer available. Please select another time.");
      }
      if (error.message.includes("time_slot_blocked")) {
        throw new Error("This date is unavailable.");
      }
      if (error.message.includes("overlap")) {
        throw new Error("This appointment slot is already booked. Please select another time.");
      }
      throw error;
    }

    // Best-effort confirmation + internal alert.
    try {
      const appt = (appointment ?? {}) as Record<string, any>;
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const [{ data: profile }, { data: consultant }, { data: type }] = await Promise.all([
        supabaseAdmin
          .from("customer_profiles")
          .select("full_name, email")
          .eq("id", data.customerId)
          .maybeSingle(),
        (supabaseAdmin as any)
          .from("consultants")
          .select("name")
          .eq("id", data.consultantId)
          .maybeSingle(),
        (supabaseAdmin as any)
          .from("consultation_types")
          .select("name")
          .eq("id", data.consultationTypeId)
          .maybeSingle(),
      ]);

      const when = new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        dateStyle: "full",
        timeStyle: "short",
      }).format(new Date(appt.start_time ?? data.startTime));

      const name = (profile as any)?.full_name ?? "there";
      const rows = [
        { label: "Consultation", value: (type as any)?.name ?? "Consultation" },
        { label: "Consultant", value: (consultant as any)?.name ?? "—" },
        { label: "When (IST)", value: when },
        { label: "Customer", value: name },
        { label: "Email", value: (profile as any)?.email ?? "—" },
        { label: "Notes", value: data.customerNotes || "—" },
      ];
      const key = appt.id ? String(appt.id) : undefined;

      const { notifyAdmins, notifyCustomer } = await import("@/lib/notifications/notify.server");
      await notifyCustomer((profile as any)?.email, {
        subject: "Your consultation is booked",
        title: "Consultation confirmed",
        intro: `Hi ${name}, your consultation is confirmed for ${when}.`,
        rows,
        outro: "Reply to this email if you need to reschedule.",
        idempotencyKey: key ? `appt-cust-${key}` : undefined,
      });
      await notifyAdmins({
        subject: `New consultation booking — ${name}`,
        title: "New consultation booking",
        rows,
        idempotencyKey: key ? `appt-admin-${key}` : undefined,
      });
    } catch (err) {
      console.error("[appointments] booking notification failed", err);
    }

    return appointment;
  });

export const getCustomerAppointments = createServerFn({ method: "GET" })
  .inputValidator((data) => z.object({ customerId: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => {
    const { data: appointments, error } = await supabase
      .from("appointments")
      .select(`
        *,
        consultation_types (name, duration_minutes),
        consultants (name)
      `)
      .eq("customer_id", data.customerId)
      .order("start_time", { ascending: false });

    if (error) throw error;
    return appointments;
  });

export const getAppointmentDetails = createServerFn({ method: "GET" })
  .inputValidator((data) => z.object({ appointmentId: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => {
    const { data: appointment, error } = await supabase
      .from("appointments")
      .select(`
        *,
        consultation_types (*),
        consultants (*),
        appointment_responses (*),
        consultation_notes (customer_recommendations),
        appointment_recommendations (
          *,
          mp2_dishes (*)
        )
      `)
      .eq("id", data.appointmentId)
      .single();

    if (error) throw error;
    return appointment;
  });
